/* ────────────────────────────────────────────────────────────────────
   /dashboard/affiliate-programs
   Beginner-friendly affiliate programs Faitharama members sign up for
   themselves. Each card opens the vendor signup in a new tab.
   ──────────────────────────────────────────────────────────────────── */
import { Link } from "react-router-dom";
import SiteNav from "../components/SiteNav";
import { ExternalLink, CheckCircle2, Zap, ShieldCheck, Cross, Globe2 } from "lucide-react";

const GREEN = "#2E9F3A";
const ORANGE = "#F39A1F";
const BLUE = "#287F8E";
const RED = "#C62828";
const INK = "#0F172A";

const TIERS = [
  {
    key: "tier1",
    label: "Approved By Mike",
    Icon: Zap,
    color: GREEN,
    programs: [
      { name: "Goli Nutrition",   pct: "15% + 20% sub-affiliate + free shipping", approval: "Instant",  category: "Wellness Supplements",  url: "https://goli.com/pages/affiliate-program", featured: true },
      { name: "Sun Home Eclipse Sauna", pct: "5% · approx $190 per sale (member earns ~2.5% after split)", approval: "Impact approval", category: "Red Light & Infrared Sauna", url: "https://sunhome.pxf.io/aNjejj" },
      { name: "Heyzine Flipbooks", pct: "10%", approval: "Instant", category: "Flipbook & Ebook Tool", url: "https://heyzine.com?via=michael-boyd" },
    ],
  },
  {
    key: "christian-soon",
    label: "Christian Brands",
    Icon: Cross,
    color: ORANGE,
    programs: [
      { name: "Amadi Jewelry", pct: "5%", approval: "Instant", category: "Christian Jewelry", url: "https://amadijewelry.com/?ref=gnmpuxop", coupon: "FAITHARAMA", couponNote: "Use code FAITHARAMA for an extra 10% off — yours to use as an affiliate." },
    ],
  },
];

export default function AffiliateProgramsPage() {
  return (
    <div className="min-h-screen" style={{ background: "#F7FAFF" }} data-testid="affiliate-programs-page">
      <SiteNav />

      {/* HERO */}
      <section className="px-5 md:px-8 pt-10 pb-6 max-w-5xl mx-auto">
        <Link to="/dashboard" className="text-sm font-bold uppercase tracking-wider" style={{ color: GREEN }} data-testid="affiliate-programs-back">
          ← Back to Dashboard
        </Link>
        <div className="text-[10px] tracking-[0.32em] uppercase font-bold mt-5 mb-2" style={{ color: GREEN }}>★ EARN ALONGSIDE FAITHARAMA</div>
        <h1 className="text-4xl md:text-5xl uppercase leading-[0.95] mb-3"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, color: INK }}>
          Affiliate Programs <span style={{ color: ORANGE }}>For Beginners.</span>
        </h1>
        <p className="text-base md:text-lg max-w-3xl" style={{ color: "#3D3D3D" }}>
          Apply directly to each program. They issue you your own affiliate link.
          You keep <strong>100%</strong> of what you earn — Faitharama takes nothing on these.
          Pick three you actually use. Start with the green tier — those approve instantly.
        </p>
      </section>

      {/* TIERS */}
      {TIERS.map((tier) => (
        <section key={tier.key} className="px-5 md:px-8 py-6 max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <tier.Icon className="w-6 h-6" style={{ color: tier.color }} />
            <h2 className="text-xl md:text-2xl font-bold uppercase tracking-wide" style={{ color: tier.color }}>
              {tier.label}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {tier.soon && tier.programs.length === 0 ? (
              <div className="md:col-span-2 rounded-2xl border-2 border-dashed bg-white p-8 text-center" style={{ borderColor: tier.color }}>
                <h3 className="text-lg md:text-xl font-bold mb-2" style={{ color: tier.color }}>Coming Soon</h3>
                <p className="text-sm md:text-base text-foreground/70 max-w-md mx-auto">
                  Mike is vetting Christian brands right now. New programs will land here as he approves them.
                </p>
              </div>
            ) : tier.programs.map((p) => (
              <article key={p.name}
                       data-testid={`affiliate-card-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                       className="rounded-2xl border-2 bg-white p-5 shadow-sm hover:shadow-lg transition flex flex-col"
                       style={{ borderColor: p.featured ? tier.color : "#E5E7EB" }}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h3 className="text-lg md:text-xl font-bold" style={{ color: INK }}>
                    {p.name}
                  </h3>
                  {p.featured && (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full text-white"
                          style={{ background: tier.color }}>
                      Mike's Pick
                    </span>
                  )}
                </div>
                <div className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: "#6B7280" }}>
                  {p.category}
                </div>
                <div className="text-sm space-y-1 mb-4 flex-1">
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" style={{ color: GREEN }}/>
                    <strong>Commission:</strong> <span>{p.pct}</span>
                  </div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" style={{ color: GREEN }}/>
                    <strong>Approval:</strong> <span>{p.approval}</span>
                  </div>
                  {p.coupon && (
                    <div className="mt-2 rounded-md p-2.5 border-2 border-dashed"
                         data-testid={`affiliate-coupon-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                         style={{ background: "#FEF3C7", borderColor: "#F39A1F" }}>
                      <div className="text-[10px] tracking-[0.22em] uppercase font-bold" style={{ color: "#8B2A2A" }}>
                        ★ AFFILIATE PERK · USE THIS CODE YOURSELF
                      </div>
                      <div className="font-mono font-bold text-base mt-1 select-all" style={{ color: "#0F172A" }}>
                        {p.coupon}
                      </div>
                      {p.couponNote && (
                        <div className="text-[11px] mt-1 leading-snug" style={{ color: "#0F172A" }}>
                          {p.couponNote}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <a href={p.url} target="_blank" rel="noopener noreferrer"
                   data-testid={`affiliate-apply-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                   className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 font-bold text-white transition hover:opacity-90"
                   style={{ background: tier.color }}>
                  Apply Free <ExternalLink className="w-4 h-4"/>
                </a>
              </article>
            ))}
          </div>
        </section>
      ))}

      {/* FOOTER NOTE */}
      <section className="px-5 md:px-8 pb-12 max-w-5xl mx-auto">
        <div className="rounded-2xl border-2 bg-white p-6"
             style={{ borderColor: GREEN }}>
          <h3 className="text-lg font-bold mb-2" style={{ color: GREEN }}>How to use this page</h3>
          <ol className="text-sm md:text-base space-y-1 list-decimal pl-5" style={{ color: "#3D3D3D" }}>
            <li>Pick 3 programs you actually use or believe in.</li>
            <li>Click <strong>Apply Free</strong> — it opens the vendor's signup in a new tab.</li>
            <li>Inside that vendor's dashboard, copy <em>your</em> affiliate link.</li>
            <li>Bring the link back to Faitharama and add it under <Link to="/dashboard/my-links" className="underline font-bold" style={{ color: GREEN }}>My Links</Link>.</li>
            <li>Faitharama will give you swipe copy + flipbooks + post templates to share with your personal link.</li>
          </ol>
        </div>
      </section>
    </div>
  );
}
